// =============================================================================
// addressParse.ts
// =============================================================================
// Pure parser that turns a Google Places `getDetails` result into the flat
// address parts the app stores ({ street, street2, suburb, state, postcode }).
//
// WHY THIS EXISTS AS ITS OWN TESTED FUNCTION:
//   Google sometimes returns a place whose `address_components` has NO
//   `street_number` component (e.g. when the matched place resolves to a route
//   rather than a precise rooftop address). The old inline parser then built the
//   street from `route` alone, silently dropping the number — so a delivery
//   address like "27 Guthrie Street" was saved as just "Guthrie Street".
//
//   Fix: when `street_number` is absent, fall back to the autocomplete
//   PREDICTION's `main_text` — i.e. the line the user actually clicked, which
//   almost always contains the number they saw. `numberMissing` is returned so
//   the UI can warn the user to confirm/override when we still couldn't recover a
//   leading number (warn-but-allow). Extracted out of AddressAutocomplete.jsx so
//   the behaviour is pinned by unit tests, not smoke testing.
// =============================================================================

export interface GoogleAddressComponent {
  long_name: string;
  short_name: string;
  types: string[];
}

export interface PredictionLike {
  structured_formatting?: { main_text?: string };
}

export interface ParsedAddress {
  street: string;
  street2: string;
  suburb: string;
  state: string;
  postcode: string;
  /** true when the resolved street line has no leading number — UI should warn. */
  numberMissing: boolean;
}

export function parsePlaceAddress(
  components: GoogleAddressComponent[] | null | undefined,
  prediction?: PredictionLike | null
): ParsedAddress {
  const comps: GoogleAddressComponent[] = components || [];

  function get(type: string): string {
    const c = comps.find((c) => c.types.indexOf(type) !== -1);
    return c ? c.long_name : "";
  }
  function getShort(type: string): string {
    const c = comps.find((c) => c.types.indexOf(type) !== -1);
    return c ? c.short_name : "";
  }

  const streetNum = get("street_number");
  const streetName = get("route");
  const mainText = prediction?.structured_formatting?.main_text || "";

  // Prefer the structured number+route. If Google gave no street_number, fall
  // back to the prediction's main_text (what the user clicked, usually with the
  // number), then to the bare route.
  let street: string;
  if (streetNum) {
    street = `${streetNum} ${streetName}`;
  } else {
    street = mainText || streetName;
  }

  // Only flag a problem if, after the fallback, the street still has no leading
  // number — i.e. we genuinely couldn't recover one. (If main_text supplied the
  // number, no warning is needed and the common case stays silent.)
  const numberMissing = !/^\s*\d/.test(street);

  return {
    street,
    street2: get("subpremise") || "",
    suburb: get("locality") || get("sublocality"),
    state: getShort("administrative_area_level_1"),
    postcode: get("postal_code"),
    numberMissing,
  };
}
